import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../apiService';
import './FlowServiceAnalyzer.css';
import { severityHierarchy } from './FlowServiceAnalyzer';
import SeveritySelector from './SeveritySelector';

interface LogEvent {
  timestamp: string;
  eventType: string;
  callId: string;
  thread: string;
  pod: string;
  duration: number;
  methodName: string;
  message: string;
  severity: string;
  details: any;
}

interface AnalysisStats {
  totalEvents: number;
  avgDuration: number;
  slowestEvent: number;
  fastestEvent: number;
  uniqueThreads: number;
  eventTypes: Record<string, number>;
}

interface TranscriptionServiceAnalyzerProps {
  callId: string;
  domainId: string;
  projectId: string;
  callStartTimestamp?: string;
  callEndTimestamp?: string;
  onClose: () => void;
}

const TranscriptionServiceAnalyzer: React.FC<TranscriptionServiceAnalyzerProps> = ({ 
  callId, 
  domainId, 
  projectId,
  callStartTimestamp,
  callEndTimestamp,
  onClose 
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<LogEvent[]>([]);
  const [stats, setStats] = useState<AnalysisStats | null>(null);
  const [expandedEvent, setExpandedEvent] = useState<number | null>(null);
  const [filterEventType, setFilterEventType] = useState('');
  const [filterThread, setFilterThread] = useState('');
  const [minDuration, setMinDuration] = useState<number>(0);
  const [severity, setSeverity] = useState('info');

  const analyzeTranscriptionServiceLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.analyzeTranscriptionServiceLogs(
        callId,
        domainId,
        projectId,
        callStartTimestamp,
        callEndTimestamp
      );
      
      setEvents(response.events || []);
      setStats(response.stats || null);
    } catch (err: any) {
      setError(err.message || 'An error occurred while analyzing logs');
    } finally {
      setLoading(false);
    }
  }, [callId, domainId, projectId, callStartTimestamp, callEndTimestamp]);

  useEffect(() => {
    analyzeTranscriptionServiceLogs();
  }, [analyzeTranscriptionServiceLogs]);

  const filteredEvents = events.filter(event => {
    if (filterEventType && !event.eventType.toLowerCase().includes(filterEventType.toLowerCase())) {
      return false;
    }
    if (filterThread && !event.thread.toLowerCase().includes(filterThread.toLowerCase())) {
      return false;
    }
    if (minDuration && event.duration < minDuration) {
      return false;
    }
    if (severity && event.severity) {
      const eventSeverityLevel = severityHierarchy[event.severity.toLowerCase()];
      const filterSeverityLevel = severityHierarchy[severity];
      if (eventSeverityLevel < filterSeverityLevel) {
        return false;
      }
    }
    return true;
  });

  const formatDuration = (duration: number) => {
    if (duration === 0) return { display: '-', className: '' };
    const ms = Math.round(duration);
    const className = ms < 100 ? 'fast' : ms < 500 ? 'medium' : 'slow';
    return { display: `${ms}ms`, className, ms };
  };

  const getEventTypeClass = (eventType: string) => {
    const type = eventType.toLowerCase().replace(/_/g, '-');
    return `event-type ${type}`;
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (loading) {
    return (
      <div className="flow-analyzer-overlay" onClick={handleOverlayClick}>
        <div className="flow-analyzer-modal" onClick={(e) => e.stopPropagation()}>
          <div className="flow-analyzer-header">
            <h3>Transcription Service Analyzer</h3>
            <button onClick={onClose} className="close-btn">×</button>
          </div>
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Analyzing transcription service logs...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flow-analyzer-overlay" onClick={handleOverlayClick}>
        <div className="flow-analyzer-modal" onClick={(e) => e.stopPropagation()}>
          <div className="flow-analyzer-header">
            <h3>Transcription Service Analyzer</h3>
            <button onClick={onClose} className="close-btn">×</button>
          </div>
          <div className="error-state">
            <p>Error: {error}</p>
            <button onClick={analyzeTranscriptionServiceLogs} className="retry-btn">
              Retry Analysis
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flow-analyzer-overlay" onClick={handleOverlayClick}>
      <div className="flow-analyzer-modal" onClick={(e) => e.stopPropagation()}>
        <div className="flow-analyzer-header">
          <h3>Transcription Service Analyzer</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        {stats && (
          <div className="stats-section">
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-number">{stats.totalEvents}</div>
                      <div className="stat-label">Total Events</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-number">{Math.round(stats.avgDuration)}ms</div>
                      <div className="stat-label">Avg Duration</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-number">{Math.round(stats.slowestEvent)}ms</div>
                      <div className="stat-label">Slowest Event</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-number">{Math.round(stats.fastestEvent)}ms</div>
                      <div className="stat-label">Fastest Event</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-number">{stats.uniqueThreads}</div>
                      <div className="stat-label">Unique Threads</div>
                    </div>
                  </div>
                </div>
        )}

        <div className="filters-section">
          <div className="filter-group">
            <label>Event Type:</label>
            <input
              type="text"
              value={filterEventType}
              onChange={(e) => setFilterEventType(e.target.value)}
              placeholder="Filter by event type"
            />
          </div>
          <div className="filter-group">
            <label>Thread:</label>
            <input
              type="text"
              value={filterThread}
              onChange={(e) => setFilterThread(e.target.value)}
              placeholder="e.g., Gax-46"
            />
          </div>
          <div className="filter-group">
            <label>Min Duration (ms):</label>
            <input
              type="number"
              value={minDuration}
              onChange={(e) => setMinDuration(Number(e.target.value))}
              placeholder="0"
            />
          </div>
          <SeveritySelector severity={severity} setSeverity={setSeverity} />
        </div>

              <div className="events-section">
                <h4>Log Events ({filteredEvents.length})</h4>
                <div className="events-table-container">
                  <table className="events-table">
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Event Type</th>
                        <th>Thread</th>
                      <th>Pod</th>
                      <th>Duration</th>
                      <th>Method</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEvents.map((event, index) => {
                      const duration = formatDuration(event.duration);
                      return (
                        <React.Fragment key={index}>
                          <tr 
                            className={`event-row ${expandedEvent === index ? 'expanded' : ''}`}
                            onClick={() => setExpandedEvent(expandedEvent === index ? null : index)}
                          >
                            <td>{new Date(event.timestamp).toLocaleTimeString()}</td>
                            <td>
                              <span className={getEventTypeClass(event.eventType)}>
                                {event.eventType}
                              </span>
                            </td>
                            <td className="thread-info">{event.thread}</td>
                            <td>{event.pod.split('-').slice(-1)[0]}</td>
                            <td>
                              <span className={`duration ${duration.className}`}>
                                {duration.display}
                              </span>
                            </td>
                            <td className="method-name">{event.methodName}</td>
                            <td>
                              <button className="expand-btn">
                                {expandedEvent === index ? '▼' : '▶'}
                              </button>
                            </td>
                          </tr>
                          {expandedEvent === index && (
                            <tr className="event-details-row">
                              <td colSpan={7}>
                                <div className="event-details">
                                  <div className="detail-section">
                                    <h5>Message:</h5>
                                    <pre>{event.message}</pre>
                                  </div>
                                  {event.details && Object.keys(event.details).length > 0 && (
                                    <div className="detail-section">
                                      <h5>Event Details:</h5>
                                      <pre>{JSON.stringify(event.details, null, 2)}</pre>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
      </div>
    </div>
  );
};

export default TranscriptionServiceAnalyzer;
